import React from 'react';
import { Box, MenuItem, Paper, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createTournament } from '../../features/tournament/tournamentSlice';

import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button } from '@mui/material';
import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';
import DatePicker from '../ui/forms/fields/datePicker';
import Checkbox from '../ui/forms/fields/checkbox';
import GameFormatDescription from '../gameFormats/description';
import OverrideGameFormat from '../gameFormats/override';

const schema = yup.object().shape({
  name: yup.string().required('Tournament name is required'),
  startDate: yup.date().required('Start date is required').nullable(),
  endDate: yup.date().required('End date is required').nullable(),
  gameFormat: yup.string().required('Game Format is required'),
  venue: yup.string(),
  hasSections: yup.bool().required('Has Sections is required'),
  hasBracketOnly: yup.bool().required('Has Bracket Only is required'),
  publishLogs: yup.bool(),
  publishScorecard: yup.bool()
});

const TournamentCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector((state) => state.tournament.loading);

  const [venues, setVenues] = React.useState([]);
  const [gameFormats, setGameFormats] = React.useState([]);

  const [selectedGameFormat, setSelectedGameFormat] = React.useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    register
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      startDate: null,
      endDate: null,
      gameFormat: '',
      venue: '',
      hasSections: false,
      hasBracketOnly: false,
      publishLogs: true,
      publishScorecard: true
    }
  });

  const handleGameFormatChange = (gameFormat) => {
    setSelectedGameFormat(gameFormat);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create Tournament
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box>
          <Input label="Name" error={errors.name} disabled={loading} {...register('name')} />
          <Box
            display="flex"
            flexDirection={'row'}
            justifyContent=""
            flexWrap="wrap"
            sx={{ mt: 2, mb: 2 }}>
            <DatePicker
              name={'startDate'}
              label="Start Date"
              control={control}
              error={errors.startDate}
              format={'DD/MM/YYYY'}
              sx={{ mr: 1 }}
              disabled={loading}
            />
            <DatePicker
              name={'endDate'}
              label="End Date"
              control={control}
              error={errors.endDate}
              format={'DD/MM/YYYY'}
              disabled={loading}
            />
          </Box>

          <Select
            label="Venue"
            name="venue"
            control={control}
            error={errors.venue}
            disabled={loading}>
            <MenuItem value={'na'}>Not Applicable</MenuItem>
            <MenuItem value={'multiple'}>Multiple</MenuItem>
            {venues.map((venue) => (
              <MenuItem key={venue.id} value={venue.id}>
                {venue.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            label="Game Format"
            name="gameFormat"
            control={control}
            error={errors.venue}
            disabled={loading}>
            {gameFormats.map((format) => (
              <MenuItem key={format.id} value={format.id}>
                {format.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {selectedGameFormat && (
          <Stack ml={2}>
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
              Game Format
            </Typography>

            {selectedGameFormat && (
              <>
                <GameFormatDescription gameFormat={selectedGameFormat} />
                <OverrideGameFormat
                  gameFormat={selectedGameFormat}
                  onChange={(gf) => handleGameFormatChange(gf)}
                />
              </>
            )}
          </Stack>
        )}

        <Stack>
          <Typography variant="h6" component="h2" mt={3}>
            Other Settings
          </Typography>

          <Checkbox
            name={'hasBracketOnly'}
            label="Bracket Only"
            control={control}
            error={errors.hasBracketOnly}
            disabled={loading}
          />
          <Checkbox
            name={'hasSections'}
            label="Sectional Play"
            control={control}
            error={errors.hasSections}
            disabled={loading}
          />
          <Checkbox
            name={'publishLogs'}
            label="Publish the logs"
            control={control}
            error={errors.publishLogs}
            disabled={loading}
          />
          <Checkbox
            name={'publishScorecard'}
            label="Publish the detailed score card"
            control={control}
            error={errors.publishScorecard}
            disabled={loading}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default TournamentCreate;
